/** Synthetic library id for legacy sample SCORM / file preview APIs (CEE-838). */
export const LEGACY_SAMPLE_PREVIEW_LIBRARY_ID = 'sample-preview';

/** Course slug for the legacy sample preview hub under /courses (CEE-838). */
export const LEGACY_SAMPLE_PREVIEW_COURSE_SLUG = 'introducing-compliance-education';

/** React Router state key for sample preview page entry attribution (Pendo CEE-1025). */
export type CeSamplePreviewEntry = 'banner' | 'direct_url' | 'breadcrumb';

export type CeSamplePreviewNavigationState = {
  ceSamplePreviewEntry?: CeSamplePreviewEntry;
};

export const LEGACY_SAMPLE_PREVIEW_BASE_PATH = `/courses/${LEGACY_SAMPLE_PREVIEW_COURSE_SLUG}`;

export function isLegacySamplePreviewPath(pathname: string): boolean {
  return pathname === LEGACY_SAMPLE_PREVIEW_BASE_PATH || pathname.startsWith(`${LEGACY_SAMPLE_PREVIEW_BASE_PATH}/`);
}

const LEGACY_SAMPLE_PREVIEW_ROOT = 'legacy-sample-preview/';
const LEGACY_SAMPLE_S3_PREFIX = 'assets/legacy-sample-preview/';
const LEGACY_SAMPLE_CUSTOM_CONTENT_PREFIX = 'custom-content/';
const LEGACY_SAMPLE_MODULES_PREFIX = 'modules/';

function hasLegacySamplePreviewContentSegment(pathAfterRoot: string): boolean {
  return (
    pathAfterRoot.startsWith(LEGACY_SAMPLE_CUSTOM_CONTENT_PREFIX) ||
    pathAfterRoot.startsWith(LEGACY_SAMPLE_MODULES_PREFIX)
  );
}

/** Catalog s3_key omits custom-content/ for SCORM zips and modules/ for additional assets. */
function resolveLegacySamplePreviewRelativePath(pathAfterRoot: string): string {
  if (hasLegacySamplePreviewContentSegment(pathAfterRoot)) {
    return pathAfterRoot;
  }

  const contentPrefix = pathAfterRoot.includes('/')
    ? LEGACY_SAMPLE_MODULES_PREFIX
    : LEGACY_SAMPLE_CUSTOM_CONTENT_PREFIX;

  return `${contentPrefix}${pathAfterRoot}`;
}

/** Prefix sample catalog keys for file preview when the key is a relative modules path. */
export function toLegacySamplePreviewFilePreviewKey(catalogKey: string): string {
  const normalized = catalogKey.replace(/\\/g, '/').replace(/^\/+/, '');

  if (normalized.startsWith(LEGACY_SAMPLE_S3_PREFIX)) {
    const pathAfterRoot = normalized.substring(LEGACY_SAMPLE_S3_PREFIX.length);
    return `${LEGACY_SAMPLE_S3_PREFIX}${resolveLegacySamplePreviewRelativePath(pathAfterRoot)}`;
  }

  if (normalized.startsWith(LEGACY_SAMPLE_PREVIEW_ROOT)) {
    const pathAfterRoot = normalized.substring(LEGACY_SAMPLE_PREVIEW_ROOT.length);
    return `${LEGACY_SAMPLE_S3_PREFIX}${resolveLegacySamplePreviewRelativePath(pathAfterRoot)}`;
  }

  return `${LEGACY_SAMPLE_S3_PREFIX}${resolveLegacySamplePreviewRelativePath(normalized)}`;
}

/** SCORM preview key (no .zip, no assets/ prefix) for /scorm-preview. */
export function toLegacySamplePreviewScormPreviewKey(catalogKey: string): string {
  const normalized = catalogKey.replace(/\\/g, '/').replace(/^\/+/, '');
  const withoutZip = normalized.replace(/\.zip$/i, '');

  if (withoutZip.startsWith(LEGACY_SAMPLE_PREVIEW_ROOT)) {
    const pathAfterRoot = withoutZip.substring(LEGACY_SAMPLE_PREVIEW_ROOT.length);
    return `${LEGACY_SAMPLE_PREVIEW_ROOT}${resolveLegacySamplePreviewRelativePath(pathAfterRoot)}`;
  }

  if (withoutZip.startsWith(LEGACY_SAMPLE_S3_PREFIX)) {
    const pathAfterRoot = withoutZip.substring(LEGACY_SAMPLE_S3_PREFIX.length);
    return `${LEGACY_SAMPLE_PREVIEW_ROOT}${resolveLegacySamplePreviewRelativePath(pathAfterRoot)}`;
  }

  return `${LEGACY_SAMPLE_PREVIEW_ROOT}${resolveLegacySamplePreviewRelativePath(withoutZip)}`;
}
